import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchTaxMasters } from "../../../slice/taxMaster/taxMasterSlice";
import api from "../../../api/axios";
import { uploadImages } from "../../../api/media";
import { toast } from "react-toastify";
import BannerCropModal from "../banner/BannerCropModal";
import StoreScopePicker, { defaultStoreScope } from "../component/StoreScopePicker";

const UNITS = ["ml", "L", "g", "kg", "pcs"];

/* canonical grind/roast types — must match the mobile API's values */
const MILLING_TYPE_OPTIONS = ["VeryCoarse", "Coarse", "Medium", "Fine", "SuperFine"];
const ROAST_TYPE_OPTIONS = ["Light", "Medium", "Dark"];
const typeLabel = (t) => t.replace(/([a-z])([A-Z])/g, "$1 $2"); // "VeryCoarse" → "Very Coarse"
/* legacy stored unit spellings → current options */
const normalizeUnit = (u) => ({ gram: "g", grams: "g", lit: "L", l: "L" }[String(u || "").toLowerCase()] || u || "g");

/* ── FSSAI food type — the mark is drawn, never an emoji ── */
const FOOD_TYPE_OPTIONS = [
  { value: "veg",    label: "Veg",          color: "#0A8F3C", bg: "#EAF6EF", border: "#B7E0C6" },
  { value: "nonveg", label: "Non-veg",      color: "#C6362C", bg: "#FBECEA", border: "#F0C3BD" },
  { value: "egg",    label: "Contains egg", color: "#C9952A", bg: "#FBF3E2", border: "#EFD18C" },
];
/* absent/unknown values fall back to veg — the catalog is vegetarian by default */
const normalizeFoodType = (v) => {
  const s = String(v || "").toLowerCase();
  return FOOD_TYPE_OPTIONS.some((o) => o.value === s) ? s : "veg";
};

const AddProduct = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { taxMasters = [], loading: taxLoading } = useSelector((s) => s.taxMaster);

  /* The save posts straight to POST /admin/catalog/products with a JSON body
     rather than through the product slice — this form needs the raw response
     and the keep-adding flow, not the slice's list state. `success` is a
     counter so a second save still fires the reset effect below. */
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState(0);

  /* ── master data ── */
  const [categories,    setCategories]    = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [masterLoading, setMasterLoading] = useState(true);

  /* ── basic fields ── */
  const [name,         setName]         = useState("");
  const [description,  setDescription]  = useState("");
  const [productCode,  setProductCode]  = useState("");
  const [categoryId,   setCategoryId]   = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [subCatId,     setSubCatId]     = useState("");
  const [images,       setImages]       = useState([]);
  const [dragOver,     setDragOver]     = useState(false);

  /* ── one unit of measurement for the whole product; every variant
        quantity below is expressed in it ── */
  const [variantUnit, setVariantUnit] = useState("g");

  /* ── FSSAI veg mark; a mill/grocery catalog is vegetarian by default ── */
  const [foodType, setFoodType] = useState("veg");

  /* ── variants (each with optional SKU + prices); start with one row ── */
  const [packSizes, setPackSizes] = useState([
    { label: "", skuCode: "", mrp: "", sellingPrice: "", weightGrams: 0, status: true },
  ]);

  /* ── conditional item checkboxes (shown when subcategory.isDefault === true) ── */
  const [showItemFields, setShowItemFields] = useState(false);
  const [itemLabels, setItemLabels] = useState(["", ""]); // e.g. ["fresh milling", "fresh masala"]
  const [freshMilling, setFreshMilling] = useState(false);
  const [freshMasala, setFreshMasala] = useState(false);

  /* ── settings ── */
  const [taxStructure, setTaxStructure] = useState(null);
  const [maxOffer,     setMaxOffer]     = useState("");
  const [isMillable,   setIsMillable]   = useState(false);
  const [isRoastable,  setIsRoastable]  = useState(false);
  const [millingTypes, setMillingTypes] = useState([...MILLING_TYPE_OPTIONS]);
  const [roastTypes,   setRoastTypes]   = useState([...ROAST_TYPE_OPTIONS]);
  /* ── highlights (GI tag + WOW item, as in the MilliyO web app) ── */
  const [isGiTagged,     setIsGiTagged]     = useState(false);
  const [giInfoUrl,      setGiInfoUrl]      = useState("");
  const [isWowItem,      setIsWowItem]      = useState(false);
  const [wowDescription, setWowDescription] = useState("");
  const [status,       setStatus]       = useState(true);
  /* which stores of the estate list this product */
  const [storeScope,   setStoreScope]   = useState(defaultStoreScope());

  /* ═══════════════════════════════════════════════════════════════
     TWO SALE CHANNELS

     A product is not "either/or" any more. The same Byadgi chilli can
     be milled to order from a bulk bin AND sold as sealed pouches, so
     the old single product-type choice is now two checkboxes:

       sellAsPacks → channels.prepacked   → the pack rows, stock in packs
       sellAsBulk  → channels.bulkMilling → the bin, held in grams and
                     priced per kg

     At least one must be on. The two stock pools are independent —
     nothing in this form (or anywhere in this system) packs bulk stock
     into pouches or pours pouches back into the bin.
  ═══════════════════════════════════════════════════════════════ */
  const [sellAsPacks, setSellAsPacks] = useState(true);
  const [sellAsBulk,  setSellAsBulk]  = useState(false);

  /* bulk-only products are the ones the server files under the fixed
     Millables taxonomy — a product that also sits on the shelf keeps
     the category the admin picked */
  const bulkOnly = sellAsBulk && !sellAsPacks;

  const toggleSellAsPacks = () => setSellAsPacks((v) => !v);
  const toggleSellAsBulk  = () =>
    setSellAsBulk((v) => {
      const next = !v;
      if (next) {
        /* selling from the bulk bin means milling to order */
        if (!isMillable) { setMillingTypes([...MILLING_TYPE_OPTIONS]); setIsMillable(true); }
      } else {
        setIsMillable(false);
        setIsRoastable(false);
      }
      return next;
    });

  /* ── validation errors ── */
  const [errors, setErrors] = useState({});

  /* ── dropdown state ── */
  const [catOpen,    setCatOpen]    = useState(false);
  const [catSearch,  setCatSearch]  = useState("");
  const [subCatOpen, setSubCatOpen] = useState(false);
  const [taxOpen,    setTaxOpen]    = useState(false);
  const [taxSearch,  setTaxSearch]  = useState("");

  const catRef    = useRef(null);
  const subCatRef = useRef(null);
  const taxRef    = useRef(null);

  /* ── fetch masters ── */
  useEffect(() => {
    const load = async () => {
      try {
        setMasterLoading(true);
        /* {data:[{id,name,slug,…}], pagination:{…}} — the payload keeps its
           own `data` key, so this read survives; limit is clamped to 100. */
        const catRes = await api.get("/admin/catalog/categories?limit=100");
        setCategories(catRes.data?.data ?? []);
      } catch (e) {
        console.error("Failed to load categories:", e);
      } finally {
        setMasterLoading(false);
      }
    };
    load();
    /* GET /admin/tax-masters answers {taxMasters:[…]} — the rates that exist,
       not a TaxMaster table: legacy per-product percentages and dated HSN
       rates, told apart by `source`. It takes no page/limit; the slice accepts
       and ignores these. */
    dispatch(fetchTaxMasters({ page: 1, limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (!categoryId) { setSubCategories([]); setSubCatId(""); return; }
    /* {success,data:[{id,name}]} — already filtered to this category and to
       active rows server-side, and the rows carry no `category` field, so the
       old client-side filter would empty the dropdown. */
    api.get(`/admin/catalog/categories/${categoryId}/subcategories`)
      .then((r) => setSubCategories(r.data?.data ?? []))
      .catch(() => setSubCategories([]));
    setSubCatId("");
    // reset item fields whenever category changes
    setShowItemFields(false);
    setItemLabels(["", ""]);
    setFreshMilling(false);
    setFreshMasala(false);
  }, [categoryId]);

  /* ── keep-adding flow: on save, clear the product-specific fields but keep
        category/sub-category/unit/tax so the admin can enter the next one ── */
  useEffect(() => {
    if (!success) return;
    toast.success("Product saved — add the next one");
    setName("");
    setProductCode("");
    setDescription("");
    setImages([]);
    setPackSizes([{ label: "", skuCode: "", mrp: "", sellingPrice: "", weightGrams: 0, status: true }]);
    setFoodType("veg");
    setMaxOffer("");
    setSellAsPacks(true);
    setSellAsBulk(false);
    setIsMillable(false);
    setIsRoastable(false);
    setMillingTypes([...MILLING_TYPE_OPTIONS]);
    setRoastTypes([...ROAST_TYPE_OPTIONS]);
    setIsGiTagged(false);
    setGiInfoUrl("");
    setIsWowItem(false);
    setWowDescription("");
    setFreshMilling(false);
    setFreshMasala(false);
    setStoreScope(defaultStoreScope());
    setErrors({});
  }, [success]);

  useEffect(() => {
    const handler = (e) => {
      if (catRef.current    && !catRef.current.contains(e.target))    { setCatOpen(false);    setCatSearch(""); }
      if (subCatRef.current && !subCatRef.current.contains(e.target)) { setSubCatOpen(false); }
      if (taxRef.current    && !taxRef.current.contains(e.target))    { setTaxOpen(false);    setTaxSearch(""); }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredCategories = categories.filter((c) =>
    c.name?.toLowerCase().includes(catSearch.toLowerCase())
  );
  const filteredTaxMasters = taxMasters.filter((t) =>
    (t.name ?? "").toLowerCase().includes(taxSearch.toLowerCase())
  );

  /* ── image helpers — every picked photo goes through a fixed 1:1 crop
        (product cards on mobile are square), one file at a time ── */
  const [cropQueue, setCropQueue] = useState([]);
  const addImages = (files) => {
    const picked = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (picked.length) setCropQueue((prev) => [...prev, ...picked]);
  };
  const handleCropped = (file) => {
    setImages((prev) => [...prev, { file, preview: URL.createObjectURL(file) }]);
    setCropQueue((prev) => prev.slice(1));
  };
  const skipCrop = () => setCropQueue((prev) => prev.slice(1));
  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  /* ── pack size helpers ── */
  const addPackSize = () =>
    setPackSizes((prev) => [...prev, { label: "", skuCode: "", mrp: "", sellingPrice: "", weightGrams: 0, status: true }]);
  const removePackSize = (idx) => setPackSizes((prev) => prev.filter((_, i) => i !== idx));
  const updatePackSize = (idx, field, value) =>
    setPackSizes((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      // editing the label invalidates any stored weight — server re-parses it from the new label
      if (field === "label") return { ...r, label: value, weightGrams: 0 };
      // MRP autofills the selling price while the seller hasn't typed one of their own
      if (field === "mrp") {
        const follows = r.sellingPrice === "" || r.sellingPrice === r.mrp;
        return follows ? { ...r, mrp: value, sellingPrice: value } : { ...r, mrp: value };
      }
      return { ...r, [field]: value };
    }));

  /* ── validate ── */
  const validate = () => {
    const errs = {};

    const hasPackSizes = packSizes.some((r) => r.label.trim());

    if (!name.trim())         errs.name        = "Product name is required.";
    if (!description.trim())  errs.description = "Description is required.";

    // A product has to sell SOME way — packs, bulk, or both.
    if (!sellAsPacks && !sellAsBulk) {
      errs.channels = "Pick at least one sale channel — packs, bulk, or both.";
    }

    // Bulk-only products land in the fixed Millables taxonomy server-side,
    // so no category selection is needed for them. Anything on the shelf
    // needs a real category to be filed under.
    if (sellAsPacks) {
      if (!categoryId) errs.category    = "Please select a category.";
      if (!subCatId)   errs.subCategory = "Please select a sub category.";
      // Variants are the only source of pack data — at least one row needed.
      if (!hasPackSizes) errs.packSizes = "Add at least one variant.";
      else if (packSizes.some((r) => !r.label.trim()))
        errs.packSizes = "Every variant needs a quantity (e.g. 200).";
    }

    // Mirrors the backend rule: restricted scope needs a non-empty whitelist.
    if (!storeScope.availableInAllStores && storeScope.stores.length === 0) {
      errs.storeScope = "Select at least one store, or make it available in all stores.";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    /* POST /admin/catalog/products binds a JSON body — multipart is a 415 —
       so the payload is a plain object. `createdBy` is gone: the server takes
       the actor from the bearer token. */
    const body = {};
    body.name        = name.trim();
    body.description = description;
    if (productCode.trim()) body.productCode = productCode.trim();
    // bulk-only: server assigns Millables → Grains/Pulses | Spices.
    // Anything on the shelf keeps the category the admin chose.
    if (sellAsPacks) {
      body.category = categoryId;
      if (subCatId) body.subCategory = subCatId;
    }

    /* The channel truth. `channels` is what the server reads; `productType`
       is only its fallback when channels is absent, and is kept in step:
         packs on  → "prepacked"  (keeps the chosen category, reaches the shelf)
         bulk only → "rawMaterial" (Millables auto-assignment, off the shelf)
       isMillable stays the flag the milling list queries either way. */
    body.channels = { prepacked: sellAsPacks, bulkMilling: sellAsBulk };

    if (!sellAsPacks) {
      /* Bulk only — sold loose by weight. The server writes the per-kg BULK
         line itself (sku 'BULK', 1000 g basis) off channels.bulkMilling, so
         there is no variant row and no legacy `variantNames` to send; the
         bin's price is set from the Stock Entry screen. */
      body.productType = "rawMaterial";
      body.variants = [];
    } else {
      body.productType = "prepacked";
      // Variants — a numeric-only quantity gets the unit of measurement
      // appended ("200" + g → "200 g").
      const code = productCode.trim();
      const withUnit = (label) =>
        /^\d+(\.\d+)?$/.test(label) ? `${label} ${variantUnit}` : label;
      body.variants = packSizes
        .filter((r) => r.label.trim())
        .map((r) => {
          const label = withUnit(r.label.trim());
          return {
            label,
            weightGrams: Number(r.weightGrams) || 0,
            // empty SKU cascades from the product code: "SKU-001" + "200 g" → "SKU-001-200g"
            skuCode: r.skuCode.trim() || (code ? `${code}-${label.replace(/\s+/g, "")}` : ""),
            // rupees — the server converts to paise itself
            mrp: r.mrp === "" ? 0 : Number(r.mrp),
            sellingPrice: r.sellingPrice === "" ? 0 : Number(r.sellingPrice),
            status: r.status !== false,
          };
        });
    }

    if (showItemFields) {
      body.freshMilling = freshMilling;
      body.freshMasala  = freshMasala;
    }

    body.foodType = normalizeFoodType(foodType);

    /* The server stores tax flat (taxPct + taxName); the picker's `id` is a
       synthetic key no write route accepts, so the chosen rate is sent as its
       two values. */
    if (taxStructure) {
      body.taxName       = taxStructure.name ?? "";
      body.taxPercentage = Number(taxStructure.percentage ?? 0);
    }
    body.maxOffer = maxOffer !== "" ? Number(maxOffer) : 0;
    /* milling flags only mean anything on the bulk channel — a
       packs-only product must never leak into the milling list */
    body.isMillable   = sellAsBulk && isMillable;
    body.isRoastable  = sellAsBulk && isRoastable;
    body.millingTypes = millingTypes;
    body.roastTypes   = roastTypes;
    body.isGiTagged   = isGiTagged;
    body.giInfoUrl    = giInfoUrl.trim();
    body.isWowItem    = isWowItem;
    body.wowDescription = wowDescription;
    body.status       = status;
    /* store scope — real arrays now that the body is JSON */
    body.availableInAllStores = storeScope.availableInAllStores;
    body.stores          = storeScope.stores;
    body.excludedStores  = storeScope.excludedStores;

    setLoading(true);
    setError("");
    try {
      /* `images` is a list of relative uploads paths on the service. The picked
         files go up through /admin/media/products first, in the order they were
         picked, so the first tile stays the first tile. The key is omitted when
         nothing was picked. */
      if (images.length > 0)
        body.images = await uploadImages(images.map((i) => i.file), "products");
      /* 201 with { message, data:{ id, packs, bulkItem } }. Errors carry a
         real HTTP status and { code, message } — there is no status field in
         the body to read. */
      await api.post("/admin/catalog/products", body);
      setSuccess((n) => n + 1);
    } catch (e) {
      setError(typeof e === "string" ? e : e.response?.data?.message || "Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  /* ── style helpers ── */
  const triggerStyle = (isOpen, hasValue, hasError) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "8px 12px",
    border: hasError ? "1.5px solid #ef4444" : isOpen ? "1.5px solid var(--mly-accent)" : "1px solid var(--mly-border)",
    borderRadius: 8, background: "var(--mly-surface)", cursor: "pointer",
    fontSize: 14, color: hasValue ? "var(--mly-text)" : "var(--mly-text-muted)",
    userSelect: "none", transition: "border 0.15s", minHeight: 38,
  });
  const panelStyle = {
    position: "absolute", top: "calc(100% + 4px)", left: "12px", right: 0,
    background: "var(--mly-surface)", border: "1.5px solid var(--mly-accent)", borderRadius: 10,
    boxShadow: "0 4px 20px rgba(14,58,44,0.13)", zIndex: 200, overflow: "hidden", width: "94%",
  };
  const itemStyle = (sel) => ({
    display: "flex", alignItems: "center", gap: 10, padding: "9px 16px",
    fontSize: 13.5, cursor: "pointer",
    color: sel ? "var(--mly-heading)" : "var(--mly-text)", fontWeight: sel ? 600 : 400,
    background: sel ? "var(--mly-accent-soft)" : "transparent",
  });
  const dotStyle = (sel) => ({
    width: 8, height: 8, borderRadius: "50%",
    background: sel ? "var(--mly-accent)" : "var(--mly-border)", flexShrink: 0,
  });
  const hoverOn  = (e, sel) => { if (!sel) e.currentTarget.style.background = "var(--mly-surface-2)"; };
  const hoverOff = (e, sel) => { if (!sel) e.currentTarget.style.background = "transparent"; };

  const ErrMsg = ({ field }) =>
    errors[field]
      ? <div style={{ color: "#ef4444", fontSize: 11, marginTop: 4, fontWeight: 500 }}>⚠ {errors[field]}</div>
      : null;

  if (masterLoading) {
    return (
      <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div>
      {/* square crop for product photos — mobile product cards are 1:1 */}
      {cropQueue.length > 0 && (
        <BannerCropModal
          file={cropQueue[0]}
          aspect={1}
          onCancel={skipCrop}
          onCropped={handleCropped}
        />
      )}
      <div className="container-fluid">
        {error && <div className="alert alert-danger mb-3">{error}</div>}

        <div className="row">

          {/* ══ LEFT ══ */}
          <div className="col-xl-8 col-lg-7">
            <div className="card padding">
              <div className="card-header">
                <h5 style={{ margin: 0, fontWeight: 700 }}>📦 Add Product</h5>
              </div>
              <div className="card-body">

                {/* ══ SALE CHANNELS ══
                    Two checkboxes, not a single choice: the same product can
                    sit on the shelf in pouches AND be milled to order from a
                    bulk bin. The two stock pools stay independent. */}
                <div className="mb-3">
                  <label className="form-label" style={LABEL}>
                    Sale Channels <span style={{ color: "#ef4444" }}>*</span>
                    <span style={{ fontWeight: 400, color: "var(--mly-text-muted)", marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>
                      pick at least one — a product may sell both ways
                    </span>
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 4 }}>
                    {[
                      {
                        on: sellAsPacks, toggle: toggleSellAsPacks,
                        title: "Sell as packs (shelf)",
                        hint:  "sealed pouches — stock per pack",
                        color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0",
                      },
                      {
                        on: sellAsBulk, toggle: toggleSellAsBulk,
                        title: "Sell as bulk for milling",
                        hint:  "loose from the bin — priced per kg",
                        color: "#b45309", bg: "#fff7ed", border: "#fed7aa",
                      },
                    ].map(({ on, toggle, title, hint, color, bg }) => (
                      <div
                        key={title}
                        onClick={() => { toggle(); if (errors.channels) setErrors((v) => ({ ...v, channels: "" })); }}
                        style={{
                          flex: "1 1 240px", display: "flex", alignItems: "flex-start", gap: 10,
                          padding: "12px 14px", borderRadius: 12, cursor: "pointer", userSelect: "none",
                          background: on ? bg : "var(--mly-surface)",
                          border: `1.5px solid ${on ? color : errors.channels ? "#ef4444" : "var(--mly-border)"}`,
                          transition: "all .15s",
                        }}
                      >
                        <div style={{
                          width: 20, height: 20, borderRadius: 5, marginTop: 1,
                          border: `2px solid ${on ? color : "var(--mly-border)"}`, background: on ? color : "var(--mly-surface)",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                          {on && (
                            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                              <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <div style={{ fontSize: 13.5, fontWeight: on ? 700 : 500, color: on ? color : "var(--mly-text)" }}>{title}</div>
                          <div style={{ fontSize: 11.5, color: on ? "#6b7280" : "var(--mly-text-muted)", marginTop: 2 }}>{hint}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <ErrMsg field="channels" />
                  {sellAsPacks && sellAsBulk && (
                    <div style={{ fontSize: 11.5, color: "var(--mly-text-soft)", marginTop: 8 }}>
                      Pack stock and bulk stock are separate pools — this system never moves stock between them.
                    </div>
                  )}
                </div>

                {sellAsPacks && (<>
                {/* Category + Sub Category */}
                <div className="row">
                  <div className="col-md-6 mb-3" ref={catRef} style={{ position: "relative" }}>
                    <label className="form-label" style={LABEL}>
                      Category <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <div
                      onClick={() => { setCatOpen((o) => !o); setCatSearch(""); if (errors.category) setErrors((v) => ({ ...v, category: "" })); }}
                      style={triggerStyle(catOpen, !!categoryId, !!errors.category)}>
                      <span>{categoryName || "Select Category"}</span>
                      <span style={{ color: "var(--mly-accent)", fontSize: 11, transform: catOpen ? "rotate(180deg)" : "none", transition: "transform 0.18s" }}>▼</span>
                    </div>
                    <ErrMsg field="category" />
                    {catOpen && (
                      <div style={panelStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", borderBottom: "1px solid var(--mly-border-soft)" }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="search-icon" aria-hidden="true"><path d="M10.6 2.4a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4Zm0 2.2a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm5.9 11.3 4.8 4.8a1.2 1.2 0 0 1-1.7 1.7l-4.8-4.8a9.6 9.6 0 0 0 1.7-1.7Z" /></svg>
                          <input autoFocus type="text" placeholder="Search..."
                            value={catSearch} onChange={(e) => setCatSearch(e.target.value)}
                            style={{ border: "none", outline: "none", width: "100%", fontSize: 13, background: "transparent" }} />
                        </div>
                        <div style={{ maxHeight: 210, overflowY: "auto", padding: "4px 0" }}>
                          {filteredCategories.length === 0
                            ? <div style={{ padding: "14px 16px", fontSize: 13, color: "var(--mly-text-muted)", textAlign: "center" }}>No results</div>
                            : filteredCategories.map((c) => {
                                const sel = categoryId === c.id;
                                return (
                                  <div key={c.id}
                                    onClick={() => { setCategoryId(c.id); setCategoryName(c.name); setCatOpen(false); setCatSearch(""); setErrors((v) => ({ ...v, category: "" })); }}
                                    style={itemStyle(sel)}
                                    onMouseEnter={(e) => hoverOn(e, sel)}
                                    onMouseLeave={(e) => hoverOff(e, sel)}>
                                    <span style={dotStyle(sel)} />{c.name}
                                  </div>
                                );
                              })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3" ref={subCatRef} style={{ position: "relative" }}>

                    <label className="form-label" style={LABEL}>
                      Sub Category <span style={{ color: "#ef4444" }}>*</span>
                    </label>

                    <div
                      onClick={() => {
                        if (categoryId) setSubCatOpen((o) => !o);
                      }}
                      style={{
                        ...triggerStyle(
                          subCatOpen,
                          !!subCatId,
                          !!errors.subCategory
                        ),
                        opacity: categoryId ? 1 : 0.5,
                        cursor: categoryId ? "pointer" : "not-allowed"
                      }}
                    >
                      <span>
                        {subCategories.find((s) => s.id === subCatId)?.name ||
                          "Select Sub Category"}
                      </span>

                      <span
                        style={{
                          color: "var(--mly-accent)",
                          fontSize: 11,
                          transform: subCatOpen ? "rotate(180deg)" : "none",
                          transition: "transform 0.18s"
                        }}
                      >
                        ▼
                      </span>
                    </div>

                    <ErrMsg field="subCategory" />

                    {subCatOpen && (
                      <div style={panelStyle}>
                        <div style={{ maxHeight: 210, overflowY: "auto", padding: "4px 0" }}>

                          {subCategories.length === 0 ? (

                            <div
                              style={{
                                padding: "14px 16px",
                                fontSize: 13,
                                color: "var(--mly-text-muted)",
                                textAlign: "center"
                              }}
                            >
                              No sub-categories
                            </div>

                          ) : (

                            subCategories.map((s) => {

                              const sel = subCatId === s.id;

                              return (
                                <div
                                  key={s.id}
                                  onClick={() => {

                                    setSubCatId(s.id);

                                    setSubCatOpen(false);

                                    if (errors.subCategory) {
                                      setErrors((v) => ({
                                        ...v,
                                        subCategory: ""
                                      }));
                                    }

                                    /* TODO(cutover): server does not return
                                       subcategory.isDefault — no subcategory
                                       route selects such a column, so the
                                       Fresh Milling / Fresh Masala pair has no
                                       trigger and stays hidden rather than
                                       being shown on a guess. */
                                    setShowItemFields(false);
                                    setItemLabels(["", ""]);
                                    setFreshMilling(false);
                                    setFreshMasala(false);
                                  }}
                                  style={itemStyle(sel)}
                                  onMouseEnter={(e) => hoverOn(e, sel)}
                                  onMouseLeave={(e) => hoverOff(e, sel)}
                                >
                                  <span style={dotStyle(sel)} />
                                  {s.name}
                                </div>
                              );
                            })

                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </>)}
                {/* bulk-only products are categorised automatically */}
                {bulkOnly && (
                  <div className="mb-3" style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "12px 16px", borderRadius: 12,
                    background: "var(--mly-accent-soft)", border: "1px solid var(--mly-border)",
                    fontSize: 13, color: "var(--mly-heading)", fontWeight: 600,
                  }}>
                    <span style={{ fontSize: 16 }}>🗂️</span>
                    Category auto-assigned:&nbsp;<b>Millables</b>&nbsp;→&nbsp;
                    <b>{isRoastable ? "Spices" : "Grains/Pulses"}</b>
                  </div>
                )}

                {/* Row 1: Product Name + Product Code */}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={LABEL}>
                      Product Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? "is-invalid" : ""}`}
                      placeholder="e.g. Premium Cinnamon Powder"
                      value={name}
                      onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((v) => ({ ...v, name: "" })); }}
                    />
                    <ErrMsg field="name" />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={LABEL}>
                      Product Code
                      <span style={{ fontWeight: 400, color: "rgb(255,197,0)", marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>optional</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. SKU-001"
                      value={productCode}
                      onChange={(e) => setProductCode(e.target.value)}
                    />
                  </div>
                </div>

                {/* Food type — FSSAI mark shown on every product surface in the app */}
                <div className="mb-3">
                  <label className="form-label" style={LABEL}>
                    Food Type <span style={{ color: "#ef4444" }}>*</span>
                    <span style={{ fontWeight: 400, color: "var(--mly-text-muted)", marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>
                      the FSSAI mark shown beside this product everywhere in the app
                    </span>
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 4 }}>
                    {FOOD_TYPE_OPTIONS.map((o) => {
                      const sel = foodType === o.value;
                      return (
                        <span key={o.value}
                          onClick={() => setFoodType(o.value)}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 8,
                            padding: "6px 14px", borderRadius: 20, fontSize: 12.5,
                            fontWeight: sel ? 700 : 600,
                            cursor: "pointer", userSelect: "none", transition: "all 0.15s",
                            background: sel ? o.bg : "var(--mly-surface)",
                            color: sel ? o.color : "var(--mly-text)",
                            border: `1.5px solid ${sel ? o.color : "var(--mly-border)"}`,
                          }}>
                          <FoodTypeMark type={o.value} size={16} />
                          {o.label}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Row 2: Unit of Measurement — one field for the product;
                    quantities in the variant rows below inherit it.
                    Only relevant to the pack channel. */}
                {sellAsPacks && (
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label" style={LABEL}>
                      Unit of Measurement <span style={{ color: "#ef4444" }}>*</span>
                      <span style={{ fontWeight: 400, color: "var(--mly-text-muted)", marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>
                        applies to every variant quantity below
                      </span>
                    </label>
                    <UnitSelect value={variantUnit} onChange={setVariantUnit} />
                  </div>
                </div>
                )}

                {/* Bulk channel: how the bin is priced and stocked.
                    Per-kg price and the bin quantity live on the product's
                    stock entry — Manage Stock is the one place they are set,
                    so this panel points there instead of offering a second,
                    silently-ignored copy of the same field. */}
                {sellAsBulk && (
                  <div className="mb-3" style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px", borderRadius: 10, background: "#fff7ed", border: "1px solid #fed7aa" }}>
                    <span style={{ fontSize: 16, lineHeight: "20px" }}>🌾</span>
                    <span style={{ fontSize: 13, color: "var(--milliyo-ink)", lineHeight: 1.6 }}>
                      <b>Bulk bin</b> — sold loose by weight through custom milling, priced <b>per kg</b>.
                      The main Product Code is its SKU.
                      <br />
                      Per-kg price and the opening bin quantity (in kg) are set in{" "}
                      <span
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate("/admin/products/stockentry")}
                        onKeyDown={(e) => { if (e.key === "Enter") navigate("/admin/products/stockentry"); }}
                        style={{ color: "var(--milliyo-forest)", fontWeight: 700, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}
                      >
                        Manage Stock
                      </span>{" "}
                      once the product is saved.
                      {sellAsPacks && " The bin is a pool of its own — pack stock below is counted separately."}
                    </span>
                  </div>
                )}

                {/* Variants — the pack channel */}
                {sellAsPacks && (
                <div className="mb-3">
                  <label className="form-label" style={LABEL}>
                    Variants <span style={{ color: "#ef4444" }}>*</span>
                    <span style={{ fontWeight: 400, color: "var(--mly-text-muted)", marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>
                      each variant carries its own SKU &amp; prices — quantity uses the unit above
                    </span>
                  </label>

                  {packSizes.length > 0 && (
                    <div style={{ display: "flex", gap: 8, marginBottom: 6, paddingRight: 44 }}>
                      <div style={{ flex: 1.2, fontSize: 10, fontWeight: 700, color: "var(--mly-text-muted)", textTransform: "uppercase", letterSpacing: 0.8 }}>Quantity ({variantUnit}) *</div>
                      <div style={{ flex: 1, fontSize: 10, fontWeight: 700, color: "var(--mly-text-muted)", textTransform: "uppercase", letterSpacing: 0.8 }}>SKU Code</div>
                      <div style={{ width: 90, flexShrink: 0, fontSize: 10, fontWeight: 700, color: "var(--mly-text-muted)", textTransform: "uppercase", letterSpacing: 0.8 }}>MRP (₹)</div>
                      <div style={{ width: 90, flexShrink: 0, fontSize: 10, fontWeight: 700, color: "var(--mly-text-muted)", textTransform: "uppercase", letterSpacing: 0.8 }}>Selling (₹)</div>
                    </div>
                  )}

                  {packSizes.map((row, idx) => (
                    <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                      <input type="text" className="form-control" placeholder={`e.g. 200 (${variantUnit})`} value={row.label}
                        style={{ flex: 1.2 }}
                        onChange={(e) => { updatePackSize(idx, "label", e.target.value); if (errors.packSizes) setErrors((v) => ({ ...v, packSizes: "" })); }} />
                      <input type="text" className="form-control"
                        placeholder={productCode ? `${productCode}-${row.label || "qty"}` : "e.g. SKU-001-200"}
                        value={row.skuCode}
                        style={{ flex: 1 }}
                        onChange={(e) => updatePackSize(idx, "skuCode", e.target.value)} />
                      <input type="number" className="form-control" placeholder="0" min="0" value={row.mrp}
                        style={{ width: 90, flexShrink: 0 }}
                        onChange={(e) => updatePackSize(idx, "mrp", e.target.value)} />
                      <input type="number" className="form-control" placeholder="0" min="0" value={row.sellingPrice}
                        style={{ width: 90, flexShrink: 0 }}
                        onChange={(e) => updatePackSize(idx, "sellingPrice", e.target.value)} />
                      <button onClick={() => removePackSize(idx)} title="Remove pack size"
                        style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "#fff5f5", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#fee2e2"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "#fff5f5"}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9.6 2.2h4.8a1.6 1.6 0 0 1 1.6 1.6v1.4h4.6a1 1 0 0 1 0 2h-.5l-1 12.4a2.4 2.4 0 0 1-2.4 2.2H7.3a2.4 2.4 0 0 1-2.4-2.2l-1-12.4h-.5a1 1 0 0 1 0-2h4.6V3.8a1.6 1.6 0 0 1 1.6-1.6Zm.4 3h4V4.2h-4Zm-.1 5a.9.9 0 0 0-.9.9v6a.9.9 0 0 0 1.8 0v-6a.9.9 0 0 0-.9-.9Zm4.2 0a.9.9 0 0 0-.9.9v6a.9.9 0 0 0 1.8 0v-6a.9.9 0 0 0-.9-.9Z" /></svg>
                      </button>
                    </div>
                  ))}

                  <button onClick={addPackSize}
                    style={{ padding: "8px 16px", borderRadius: 8, border: "1.5px dashed var(--mly-accent)", background: "var(--mly-accent-soft)", color: "var(--mly-heading)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    + Add variant
                  </button>
                  <ErrMsg field="packSizes" />
                </div>
                )}

                {/* Description */}
                <div className="mb-3">
                  <label className="form-label" style={LABEL}>
                    Description <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <textarea
                    className={`form-control ${errors.description ? "is-invalid" : ""}`}
                    rows={4}
                    placeholder="Enter product description..."
                    value={description}
                    onChange={(e) => { setDescription(e.target.value); if (errors.description) setErrors((v) => ({ ...v, description: "" })); }}
                    style={{ resize: "vertical" }}
                  />
                  <ErrMsg field="description" />
                </div>


                {/* Item 1 / Item 2 — checkboxes shown only when selected sub category has isDefault: true */}
                {showItemFields && (
                  <div className="mb-2">
                    <label className="form-label" style={LABEL}>Milling / Masala Category</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
                      {[
                        { val: freshMilling, set: () => { setFreshMilling(true); setFreshMasala(false); }, label: itemLabels[0], color: "var(--milliyo-forest)", bg: "var(--milliyo-green-50)", border: "#c7c5f7" },
                        { val: freshMasala,  set: () => { setFreshMasala(true);  setFreshMilling(false); }, label: itemLabels[1], color: "var(--milliyo-forest)", bg: "var(--milliyo-green-50)", border: "#c7c5f7" },
                      ].map(({ val, set, label, color, bg, border }) => (
                        <div key={label}
                          style={{ display: "flex", marginLeft: "20px", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}
                          onClick={set}>
                          <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${val ? color : "var(--mly-border)"}`, background: val ? color : "var(--mly-surface)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                            {val && (
                              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                                <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                          <span style={{ fontSize: 13.5, color: "var(--mly-text)", fontWeight: val ? 600 : 400 }}>{label}</span>
                          {val && <span style={{ background: bg, color, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, border: `1px solid ${border}` }}>YES</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* ══ RIGHT ══ */}
          <div className="col-xl-4 col-lg-5">

            {/* Product Images */}
            <div className="card padding">
              <div className="card-header">
                <h5 style={{ margin: 0, fontWeight: 700 }}>🖼️ Product Images</h5>
              </div>
              <div className="card-body">
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); addImages(e.dataTransfer.files); }}
                  onClick={() => document.getElementById("productImagesInput").click()}
                  style={{
                    border: `2px dashed ${dragOver ? "var(--mly-accent-2)" : "var(--mly-accent)"}`,
                    borderRadius: 12, background: dragOver ? "var(--mly-surface-3)" : "var(--mly-accent-soft)",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    minHeight: 130, cursor: "pointer", transition: "background 0.2s", marginBottom: 20,
                  }}>
                  <div style={{ fontSize: 30, marginBottom: 6 }}>🖼️</div>
                  <div style={{ fontWeight: 700, color: "var(--mly-text-soft)", fontSize: 13 }}>DRAG &amp; DROP or CLICK to Upload</div>
                  <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 2 }}>PNG, JPG, WEBP — Multiple allowed</div>
                </div>
                <input id="productImagesInput" type="file" accept="image/png,image/jpeg,image/webp"
                  multiple onChange={(e) => addImages(e.target.files)} style={{ display: "none" }} />

                {images.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                    {images.map((img, idx) => (
                      <div key={idx} style={{ position: "relative" }}>
                        <img src={img.preview} alt={`img-${idx}`}
                          style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 10, border: idx === 0 ? "2.5px solid var(--mly-accent)" : "1.5px solid var(--mly-border)" }} />
                        {idx === 0 && (
                          <span style={{ position: "absolute", bottom: 4, left: 4, background: "var(--milliyo-forest)", color: "#fff", fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 6 }}>MAIN</span>
                        )}
                        <button onClick={() => removeImage(idx)}
                          style={{ position: "absolute", top: -6, right: -6, width: 22, height: 22, borderRadius: "50%", border: "none", background: "#ef4444", color: "#fff", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="card padding">
              <div className="card-body">

                {/* Tax Structure */}
                <div className="mb-3" ref={taxRef} style={{ position: "relative" }}>
                  <label className="form-label" style={LABEL}>Tax Structure</label>
                  <div onClick={() => { setTaxOpen((o) => !o); setTaxSearch(""); }}
                    style={triggerStyle(taxOpen, !!taxStructure, false)}>
                    <span>
                      {taxStructure
                        ? `${taxStructure.name ?? "—"} (${taxStructure.percentage ?? 0}%)`
                        : "Select Tax"}
                    </span>
                    <span style={{ color: "var(--mly-accent)", fontSize: 11, transform: taxOpen ? "rotate(180deg)" : "none", transition: "transform 0.18s" }}>▼</span>
                  </div>
                  {taxOpen && (
                    <div style={panelStyle}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", borderBottom: "1px solid var(--mly-border-soft)" }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="search-icon" aria-hidden="true"><path d="M10.6 2.4a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4Zm0 2.2a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm5.9 11.3 4.8 4.8a1.2 1.2 0 0 1-1.7 1.7l-4.8-4.8a9.6 9.6 0 0 0 1.7-1.7Z" /></svg>
                        <input autoFocus type="text" placeholder="Search tax..."
                          value={taxSearch} onChange={(e) => setTaxSearch(e.target.value)}
                          style={{ border: "none", outline: "none", width: "100%", fontSize: 13, background: "transparent" }} />
                      </div>
                      <div style={{ maxHeight: 210, overflowY: "auto", padding: "4px 0" }}>
                        <div onClick={() => { setTaxStructure(null); setTaxOpen(false); setTaxSearch(""); }}
                          style={itemStyle(!taxStructure)}
                          onMouseEnter={(e) => hoverOn(e, !taxStructure)}
                          onMouseLeave={(e) => hoverOff(e, !taxStructure)}>
                          <span style={dotStyle(!taxStructure)} />None
                        </div>
                        {taxLoading
                          ? <div style={{ padding: "14px 16px", fontSize: 13, color: "var(--mly-text-muted)", textAlign: "center" }}>Loading...</div>
                          : filteredTaxMasters.length === 0
                            ? <div style={{ padding: "14px 16px", fontSize: 13, color: "var(--mly-text-muted)", textAlign: "center" }}>No results</div>
                            : filteredTaxMasters.map((t) => {
                                /* the row's key is `id`, a synthetic string
                                   ("legacy:5:GST 5%" / "hsn:1101:…"); `_id`
                                   is gone, and matching undefined against
                                   undefined marked every row selected */
                                const sel   = taxStructure?.id === t.id;
                                const dName = t.name ?? "—";
                                const dPct  = t.percentage ?? 0;
                                return (
                                  <div key={t.id}
                                    onClick={() => { setTaxStructure(t); setTaxOpen(false); setTaxSearch(""); }}
                                    style={itemStyle(sel)}
                                    onMouseEnter={(e) => hoverOn(e, sel)}
                                    onMouseLeave={(e) => hoverOff(e, sel)}>
                                    <span style={dotStyle(sel)} />
                                    <span>{dName}</span>
                                    <span style={{ marginLeft: "auto", fontSize: 11, color: "#a16207", background: "#fefce8", border: "1px solid #fde68a", borderRadius: 20, padding: "2px 8px", fontWeight: 700 }}>{dPct}%</span>
                                  </div>
                                );
                              })
                        }
                      </div>
                    </div>
                  )}
                </div>

                {/* Max Offer */}
                <div className="mb-3">
                  <label className="form-label" style={LABEL}>
                    Max Offer (%)

                  </label>
                  <input type="number"
                    className={`form-control ${errors.maxOffer ? "is-invalid" : ""}`}
                    placeholder="0" min="0" value={maxOffer}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || Number(v) >= 0) {
                        setMaxOffer(v);
                        if (errors.maxOffer) setErrors((ev) => ({ ...ev, maxOffer: "" }));
                      }
                    }} />
                  <ErrMsg field="maxOffer" />
                  {maxOffer !== "" && Number(maxOffer) > 0 && !errors.maxOffer && (
                    <div style={{ marginTop: 8 }}>
                      <span style={{ background: "var(--mly-accent-soft)", color: "var(--mly-heading)", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, border: "1px solid var(--mly-accent)" }}>{maxOffer}% max discount</span>
                    </div>
                  )}
                </div>

                {/* Highlights */}
                <div className="mb-3">
                  <label className="form-label" style={LABEL}>Highlights</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>

                    <div style={{ display: "flex", marginLeft: 20, alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}
                      onClick={() => setIsWowItem(!isWowItem)}>
                      <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${isWowItem ? "var(--milliyo-gold)" : "var(--mly-border)"}`, background: isWowItem ? "var(--milliyo-gold)" : "var(--mly-surface)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                        {isWowItem && (
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                            <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <span style={{ fontSize: 13.5, color: "var(--mly-text)", fontWeight: isWowItem ? 600 : 400 }}>★ WOW Item</span>
                      {isWowItem && <span style={{ background: "var(--milliyo-gold-50)", color: "var(--milliyo-gold-700, #8F5E18)", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, border: "1px solid var(--milliyo-gold-200, #EFD18C)" }}>FEATURED</span>}
                    </div>
                    {isWowItem && (
                      <textarea className="form-control" rows={2} value={wowDescription}
                        placeholder="Why is this item special? Shown on the product page."
                        onChange={(e) => setWowDescription(e.target.value)}
                        style={{ marginLeft: 20, width: "calc(100% - 20px)", fontSize: 13 }} />
                    )}

                    <div style={{ display: "flex", marginLeft: 20, alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}
                      onClick={() => setIsGiTagged(!isGiTagged)}>
                      <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${isGiTagged ? "var(--milliyo-forest)" : "var(--mly-border)"}`, background: isGiTagged ? "var(--milliyo-forest)" : "var(--mly-surface)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                        {isGiTagged && (
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                            <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <span style={{ fontSize: 13.5, color: "var(--mly-text)", fontWeight: isGiTagged ? 600 : 400 }}>GI Tagged</span>
                      {isGiTagged && <span style={{ background: "var(--milliyo-green-50)", color: "var(--milliyo-forest)", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, border: "1px solid var(--milliyo-sand)" }}>GEOGRAPHICAL INDICATION</span>}
                    </div>
                    {isGiTagged && (
                      <input type="url" className="form-control" value={giInfoUrl}
                        placeholder="Link to the official GI registry record (optional)"
                        onChange={(e) => setGiInfoUrl(e.target.value)}
                        style={{ marginLeft: 20, width: "calc(100% - 20px)", fontSize: 13 }} />
                    )}
                  </div>
                </div>

                {/* Milling options — only meaningful on the bulk channel,
                    where the grain is milled/roasted to order */}
                {sellAsBulk && (
                <div className="mb-2">
                  <label className="form-label" style={LABEL}>
                    Milling Options
                    <span style={{ fontWeight: 400, color: "var(--mly-text-muted)", marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>
                      how the bulk bin can be processed to order
                    </span>
                  </label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
                    {[
                      { val: isMillable,  toggle: () => { if (isRoastable) return; if (!isMillable) setMillingTypes([...MILLING_TYPE_OPTIONS]); setIsMillable(!isMillable); },  label: "Is Millable",  color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", options: MILLING_TYPE_OPTIONS, types: millingTypes, setTypes: setMillingTypes },
                      { val: isRoastable, toggle: () => { const next = !isRoastable; if (next) { setRoastTypes([...ROAST_TYPE_OPTIONS]); if (!isMillable) { setMillingTypes([...MILLING_TYPE_OPTIONS]); setIsMillable(true); } } setIsRoastable(next); }, label: "Is Roastable", color: "#ea580c", bg: "#fff7ed", border: "#fed7aa", options: ROAST_TYPE_OPTIONS,  types: roastTypes,   setTypes: setRoastTypes },
                    ].map(({ val, toggle, label, color, bg, border, options, types, setTypes }) => (
                      <div key={label}>
                        <div
                          style={{ display: "flex", marginLeft: "20px", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}
                          onClick={toggle}>
                          <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${val ? color : "var(--mly-border)"}`, background: val ? color : "var(--mly-surface)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                            {val && (
                              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                                <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                          <span style={{ fontSize: 13.5, color: "var(--mly-text)", fontWeight: val ? 600 : 400 }}>{label}</span>
                          {val && <span style={{ background: bg, color, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, border: `1px solid ${border}` }}>YES</span>}
                        </div>
                        {/* indented chip row — which types this product supports */}
                        {val && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginLeft: 50, marginTop: 8 }}>
                            {options.map((t) => {
                              const sel = types.includes(t);
                              return (
                                <span key={t}
                                  onClick={() => setTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t])}
                                  style={{
                                    padding: "4px 12px", borderRadius: 20, fontSize: 11.5, fontWeight: 600,
                                    cursor: "pointer", userSelect: "none", transition: "all 0.15s",
                                    background: sel ? "var(--milliyo-forest)" : "var(--mly-surface)",
                                    color: sel ? "#fff" : "var(--mly-text)",
                                    border: `1px solid ${sel ? "var(--milliyo-forest)" : "var(--mly-border)"}`,
                                  }}>
                                  {typeLabel(t)}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* Store scope — hidden automatically on single-store estates */}
                <StoreScopePicker value={storeScope} onChange={setStoreScope} compact />
                <ErrMsg field="storeScope" />

              </div>
            </div>

            {/* Action Buttons */}
            <div className="mb-4" style={{ display: "flex", gap: 12 }}>
              <button onClick={() => navigate("/admin/products")}
                className="btn btn-light" style={{ flex: 1, fontWeight: 600 }} disabled={loading}>
                Cancel
              </button>
              <button onClick={handleSave}
                className="btn btn-primary" style={{ flex: 2, fontWeight: 700 }} disabled={loading}>
                {loading
                  ? <span><span className="spinner-border spinner-border-sm me-2" role="status" />Saving...</span>
                  : "Save Product"}
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

/* ══ FSSAI Food-type Mark ══
   Square outline with a filled circle centred inside — drawn in CSS, never an
   emoji. 1.5px border, 2px corners, dot at 45% of the square's inner width. */
const FoodTypeMark = ({ type = "veg", size = 16, style }) => {
  const { color } = FOOD_TYPE_OPTIONS.find((o) => o.value === type) ?? FOOD_TYPE_OPTIONS[0];
  const dot = Math.max(3, Math.round((size - 3) * 0.45));
  return (
    <span
      aria-hidden="true"
      style={{
        width: size, height: size, flexShrink: 0, boxSizing: "border-box",
        border: `1.5px solid ${color}`, borderRadius: 2, background: "#fff",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        ...style,
      }}>
      <span style={{ width: dot, height: dot, borderRadius: "50%", background: color }} />
    </span>
  );
};

/* ══ Unit Select Component ══ */
const UnitSelect = ({ value, onChange }) => {
  const [focused, setFocused] = useState(false);
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        border: focused ? "1.5px solid var(--mly-accent)" : "1px solid var(--mly-border)",
        borderRadius: 8, fontSize: 13, color: "var(--mly-text)", background: "var(--mly-surface)",
        cursor: "pointer", padding: "4px 10px", width: 80, flexShrink: 0,
        outline: "none", transition: "border 0.15s", height: 44, fontWeight: 500,
      }}
    >
      {UNITS.map((u) => (
        <option key={u} value={u}>{u}</option>
      ))}
    </select>
  );
};

const LABEL = {
  fontSize: 11, fontWeight: 700, letterSpacing: 1,
  color: "var(--mly-text-muted)", textTransform: "uppercase",
};

export default AddProduct;